import React, { useState, useRef } from "react";
import api, { apiError } from "@/lib/api";
import CustomerLayout from "@/components/CustomerLayout";
import { Store, ArrowRight, CheckCircle2, IndianRupee, MapPin, Rocket } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  full_name: "", mobile: "", whatsapp: "", email: "", state: "", district: "",
  city: "", preferred_location: "", existing_business: "", investment_budget: "",
  commercial_space: "", timeline: "", message: "", consent: false,
};

const DISCLAIMER = "Investment starts from ₹2,00,000/-. Actual investment may vary depending on location, store size, inventory, infrastructure and business requirements.";

export default function StartStore() {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [leadId, setLeadId] = useState(null);
  const formRef = useRef(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) return toast.error("Please accept the consent to continue");
    setBusy(true);
    try {
      const { data } = await api.post("/leads", { ...form, source: "Grofkart Store Banner" });
      setLeadId(data.lead_id);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };

  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: "smooth" });

  if (leadId) {
    return (
      <CustomerLayout>
        <div className="mx-auto max-w-lg px-4 py-16 text-center" data-testid="lead-success">
          <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-brand-soft text-brand ring-8 ring-brand-soft/40"><CheckCircle2 className="h-9 w-9" /></div>
          <h1 className="font-head text-3xl font-black text-slate-900">Application received!</h1>
          <p className="mt-2 text-slate-600">Thank you for your interest in starting a Grofkart Store. Our franchise team will reach out to you shortly.</p>
          <div className="mt-5 inline-block rounded-2xl border border-slate-200 bg-white px-6 py-4">
            <p className="text-xs font-bold uppercase tracking-wide text-slate-400">Your Lead ID</p>
            <p className="font-head text-2xl font-black text-brand" data-testid="lead-id">{leadId}</p>
          </div>
          <p className="mx-auto mt-6 max-w-md text-xs text-slate-400">{DISCLAIMER}</p>
        </div>
      </CustomerLayout>
    );
  }

  const Input = ({ k, label, req, type = "text" }) => (
    <label className="block text-sm">
      <span className="mb-1 block font-semibold text-slate-600">{label}{req && <span className="text-red-500"> *</span>}</span>
      <input value={form[k]} onChange={set(k)} type={type} required={req} data-testid={`lead-${k}`}
        className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-brand" />
    </label>
  );

  return (
    <CustomerLayout>
      {/* Hero */}
      <div className="relative overflow-hidden rounded-3xl border border-slate-200" data-testid="start-store-hero">
        <img src="/franchise-banner.jpg" alt="Start your Grofkart Store" className="h-64 w-full object-cover sm:h-80" />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-deep/90 via-brand-deep/60 to-transparent" />
        <div className="absolute inset-0 flex flex-col justify-center gap-3 p-6 sm:p-10 text-white">
          <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-amber-surge px-3 py-1 text-[11px] font-bold uppercase tracking-wider"><Rocket className="h-3.5 w-3.5" /> Franchise Opportunity</span>
          <h1 className="max-w-xl font-head text-3xl font-black leading-tight sm:text-5xl">Start Your Grofkart Store</h1>
          <p className="max-w-lg text-sm font-medium text-white/90 sm:text-lg">Bring Grofkart Quick Commerce to Your Area</p>
          <p className="flex items-center gap-1.5 text-lg font-black sm:text-2xl"><IndianRupee className="h-5 w-5" />Investment Starts From ₹2,00,000/-<span className="text-sm font-medium">*</span></p>
          <button onClick={scrollToForm} data-testid="apply-now-hero-btn" className="mt-1 inline-flex w-fit items-center gap-2 rounded-xl bg-white px-6 py-3 text-sm font-bold text-brand-deep hover:bg-white/90">
            Apply Now <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="mx-auto mt-6 grid max-w-3xl gap-3 sm:grid-cols-3">
        {[[Store, "Your own store", "Run a branded Grofkart store in your locality"], [MapPin, "Local demand", "Serve quick-commerce orders from nearby customers"], [Rocket, "Full support", "Tech, catalogue, delivery & operations support"]].map(([Icon, t, d], i) => (
          <div key={i} className="rounded-2xl border border-slate-200 bg-white p-4 text-center">
            <Icon className="mx-auto mb-2 h-6 w-6 text-brand" />
            <p className="font-bold text-slate-800">{t}</p><p className="mt-0.5 text-xs text-slate-500">{d}</p>
          </div>
        ))}
      </div>

      {/* Lead form */}
      <div ref={formRef} className="mx-auto mt-8 max-w-3xl rounded-3xl border border-slate-200 bg-white p-6 sm:p-8" data-testid="lead-form">
        <h2 className="font-head text-2xl font-black text-slate-900">Apply for a Grofkart Store</h2>
        <p className="mt-1 text-sm text-slate-500">Fill in your details and our team will get in touch.</p>
        <form onSubmit={submit} className="mt-5 grid gap-4 sm:grid-cols-2">
          <Input k="full_name" label="Full Name" req />
          <Input k="mobile" label="Mobile Number" req type="tel" />
          <Input k="whatsapp" label="WhatsApp Number" type="tel" />
          <Input k="email" label="Email" type="email" />
          <Input k="state" label="State" req />
          <Input k="district" label="District" req />
          <Input k="city" label="City / Town" req />
          <Input k="preferred_location" label="Preferred Store Location" req />
          <label className="block text-sm">
            <span className="mb-1 block font-semibold text-slate-600">Existing Business</span>
            <select value={form.existing_business} onChange={set("existing_business")} data-testid="lead-existing_business" className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-brand">
              <option value="">Select</option><option value="yes">Yes</option><option value="no">No</option>
            </select>
          </label>
          <Input k="investment_budget" label="Investment Budget" />
          <label className="block text-sm">
            <span className="mb-1 block font-semibold text-slate-600">Commercial Space</span>
            <select value={form.commercial_space} onChange={set("commercial_space")} data-testid="lead-commercial_space" className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-brand">
              <option value="">Select</option><option value="own">Own</option><option value="rent">Rent</option><option value="looking">Looking</option>
            </select>
          </label>
          <Input k="timeline" label="Expected Start Timeline" />
          <label className="block text-sm sm:col-span-2">
            <span className="mb-1 block font-semibold text-slate-600">Message</span>
            <textarea value={form.message} onChange={set("message")} rows={3} data-testid="lead-message" className="w-full resize-none rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-brand" />
          </label>
          <label className="flex items-start gap-2 text-xs text-slate-600 sm:col-span-2">
            <input type="checkbox" checked={form.consent} onChange={set("consent")} data-testid="lead-consent" className="mt-0.5 h-4 w-4 accent-brand" />
            <span>I agree to be contacted by Grofkart regarding this franchise enquiry and accept the terms.</span>
          </label>
          <div className="sm:col-span-2">
            <button type="submit" disabled={busy} data-testid="lead-submit" className="w-full rounded-xl bg-brand py-3 text-sm font-bold text-white hover:bg-brand-deep disabled:opacity-60">
              {busy ? "Submitting…" : "Submit Application"}
            </button>
            <p className="mt-3 text-[11px] leading-relaxed text-slate-400">{DISCLAIMER}</p>
          </div>
        </form>
      </div>
    </CustomerLayout>
  );
}
